import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  selectUserProfile,
  selectUserLoading,
  selectUserError,
} from "../../selectors/userProfileSelectors";
import { useAppDispatch } from "../../../../store/hooks";
import { toastService as toast } from "../../../../components/ui/toast/toast.service";
import {
  DatePicker,
  PasswordInput,
  TextInput,
  ToggleSwitch,
} from "../../../../components/ui/inputs";
import { Button, Card, CardBody, CardHeader, Header4 } from "../../../../components/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { PhoneNumberInput } from "../../../../components/ui/inputs/PhoneNumberInput";
import { ContactMethods } from "../../../../models/constants/contactMethods";
import { UserProfileUpdateRequest } from "../../models/request/userProfileUpdateRequest";
import userService from "../../services/user.service";
import localization from "../../../../config/localization";
import { formatDate } from "../../../../utils/dateTimeHelper";
import { BottomDrawer } from "../../../../components/ui/BottomDrawer";
import { ChangePassword } from "../change-password/ChangePassword";
import { Avatar } from "../avatar/avatar";
import { getAvatarById } from "../avatar/avatar.config";
import { OtpModal } from "../../../../components/ui/modals/OtpModal";
import { emailOtpVerified, mobileOtpVerified } from "../../reducers/userProfileReducer";
import { fetchUserProfile } from "../../actions/userProfileActions";
import { Validations } from "../../../../models/constants/validations";
import { UserProfileResponse } from "../../models/response/userProfileResponse";
import { DeviceType } from "../../../../models/constants/deviceType";
import userDeviceStore from "../../../../store/device.store";
import { toastService } from "../../../../components/ui/toast/toast.service";
import { AccessMode } from "../../../../models/constants/accessMode";
import { localStorageService } from "../../../../services";
import { UpdateUserBiometricsRequest } from "../../models/request/updateUserBiometricsRequest";
import { Modal } from "../../../../components/ui/modals";
import { BlobContainerConsts } from "../../../../models/constants/blobContainerConsts";
import { BLOB_IMAGE_URL } from "../../../../config/appConfig";

export default function UserProfile() {

  const dispatch = useAppDispatch();

  const profile = useSelector(selectUserProfile);
  const loading = useSelector(selectUserLoading);
  const error = useSelector(selectUserError);

  const [fullName, setFullName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [mobile, setMobile] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<string | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [countryCode, setCountryCode] = useState("+91");

  const [isEditMode, setIsEditMode] = useState(false);

  const [isAllNotifications, setIsAllNotifications] = useState(false);
  const [isEmailNotifications, setIsEmailNotifications] = useState(false);
  const [isSMSNotifications, setIsSMSNotifications] = useState(false);
  const [isWhatsAppNotifications, setIsWhatsAppNotifications] = useState(false);
  const [isCallNotifications, setIsCallNotifications] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);

  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [mobileErrorMessage, setMobileErrorMessage] = useState("");
  const [fullNameErrorMessage, setFullNameErrorMessage] = useState("");
  const [dateOfBirthErrorMessage, setDateOfBirthErrorMessage] = useState("");

  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [emailVerificationErrorMessage, setEmailVerificationErrorMessage] = useState("");
  const [showMobileVerificationModal, setShowMobileVerificationModal] = useState(false);
  const [mobileVerificationErrorMessage, setMobileVerificationErrorMessage] = useState("");

  const [isBiometricsEnabled, setIsBiometricsEnabled] = useState(false);
  const [showBiometricsPasswordModal, setShowBiometricsPasswordModal] = useState(false);
  const [biometricsVerificationPassword, setBiometricsVerificationPassword] = useState<string | null>(null);

  const [avatar, setAvatar] = useState<string | null>(null);
  const [uploadedAvatar, setUploadedAvatar] = useState<File | null>(null);
  const [openChooseAvatar, setOpenChooseAvatar] = useState(false);

  const [isChanged, setIsChanged] = useState(false);

  const deviceType = userDeviceStore((state) => state.deviceType);

  const preferredContactMethodsCsv = useMemo(() => {
    let methods = [];
    if (isEmailNotifications) methods.push(ContactMethods.EMAIL);
    if (isSMSNotifications) methods.push(ContactMethods.SMS);
    if (isWhatsAppNotifications) methods.push(ContactMethods.WHATSAPP);
    if (isCallNotifications) methods.push(ContactMethods.CALL);
    return methods.join(",") ?? null;
  }, [
    isEmailNotifications,
    isSMSNotifications,
    isWhatsAppNotifications,
    isCallNotifications,
  ]);

  useEffect(() => {
    if (!loading && error) {
      toast.error("Failed to load profile");
    }
  }, [loading, error]);

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, []);

  const initializeProfile = (profile: UserProfileResponse) => {
    setUsername(profile.username ?? "");
    setFullName(profile.name ?? null);
    setMobile(profile.mobile ?? null);
    setEmail(profile.email ?? null);
    setAddress(profile.address ?? null);
    setDateOfBirth(profile.birthdate ?? null);
    setCountryCode(profile.countryCode ?? "+91");

    setIsEmailVerified(profile.emailVerified ?? false);
    setIsMobileVerified(profile.mobileVerified ?? false);
    setAvatar(profile.profilePictureUrl ?? "");
    setIsBiometricsEnabled(profile.isBiometricsEnabled ?? false);

    const methods = profile.preferredContactMethodsCsv ?? null;

    if (methods) {
      setIsEmailNotifications(methods.includes(ContactMethods.EMAIL));
      setIsSMSNotifications(methods.includes(ContactMethods.SMS));
      setIsWhatsAppNotifications(methods.includes(ContactMethods.WHATSAPP));
      setIsCallNotifications(methods.includes(ContactMethods.CALL));
    }
  };

  useEffect(() => {
    if (!profile) return;
    initializeProfile(profile);
  }, [profile]);

  useEffect(() => {
    const allEnabled =
      isEmailNotifications &&
      isSMSNotifications &&
      isWhatsAppNotifications &&
      isCallNotifications;

    setIsAllNotifications(allEnabled);
  }, [
    isEmailNotifications,
    isSMSNotifications,
    isWhatsAppNotifications,
    isCallNotifications,
  ]);

  const handleEdit = () => {
    setIsEditMode(!isEditMode);

    if (!isEditMode) {
      resetForm();
    }
  };

  const resetForm = () => {
    setIsChanged(false);
    initializeProfile(profile!);
    dispatch(fetchUserProfile());
  };

  const handleChooseAvatar = (value: string) => {
    setAvatar(value);
  };

  const handleSubmitAvatar = (file: File | null) => {
    if (file) {
      setUploadedAvatar(file);
    }
    setIsChanged(true);
    setOpenChooseAvatar(false);
  };

  const handleFullNameChange = (value: string) => {
    setFullName(value);
    setIsChanged(true);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setIsChanged(true);
  };

  const handleDateOfBirthChange = (date: string) => {
    setIsChanged(true);
    setDateOfBirth(`${date}`);
  };

  const handleMobileChange = (value: string) => {
    setMobile(value);
    setIsChanged(true);
  };

  const handleAddressChange = (value: string) => {
    setAddress(value);
    setIsChanged(true);
  };

  const handleSendEmailVerificationCode = () => {
    userService.sendEmailVerificationCodeOld().then((response) => {
      if (response.isSuccess) {
        setTimeout(() => {
          setShowEmailVerificationModal(true);
        }, 1000);
      }
    });
  };

  const handleResendEmailVerificationCode = () => {
    userService.sendEmailVerificationCodeOld().then((response) => {
      if (response.isSuccess) {
        setEmailVerificationErrorMessage("");
      }
    });
  };

  const handleVerifyEmailVerificationCode = (otp: string) => {
    userService.verifyEmailVerificationCodeOld(otp).then((response) => {
      if (response.isSuccess) {
        toast.success(localization.email_verified);
        dispatch(emailOtpVerified(true));
        setTimeout(() => {
          setShowEmailVerificationModal(false);
        }, 1000);
      }
    });
  };

  const handleCloseEmailVerificationModal = () => {
    setShowEmailVerificationModal(false);
    setEmailVerificationErrorMessage("");
  };

  const handleSendMobileVerificationCode = () => {
    userService.sendMobileVerificationCodeOld().then((response) => {
      if (response.isSuccess) {
        setTimeout(() => {
          setShowMobileVerificationModal(true);
        }, 1000);
      }
    });
  };

  const handleResendMobileVerificationCode = () => {
    userService.sendMobileVerificationCodeOld().then((response) => {
      if (response.isSuccess) {
        setMobileVerificationErrorMessage("");
      }
    });
  };

  const handleVerifyMobileVerificationCode = (otp: string) => {
    userService.verfiyMobileVerificationCodeOld(otp).then((response) => {
      if (response.isSuccess) {
        dispatch(mobileOtpVerified(true));
        toast.success(localization.mobile_verified);
        setTimeout(() => {
          setShowMobileVerificationModal(false);
        }, 1000);
      }
    });
  };

  const handleCloseMobileVerificationModal = () => {
    setShowMobileVerificationModal(false);
    setMobileVerificationErrorMessage("");
  };

  function checkIsAdult(year: number, month: number, day: number): boolean {
    const now = new Date();

    const yearDifference = now.getFullYear() - year;
    const monthDifference = now.getMonth() + 1 - month;
    const dateDifference = now.getDate() - day;

    if (
      yearDifference > 18 ||
      (yearDifference === 18 &&
        (monthDifference > 0 ||
          (monthDifference === 0 && dateDifference >= 0)))
    ) {
      return true;
    }

    return false;
  }

  const checkValidation = () => {
    setEmailErrorMessage("");
    setMobileErrorMessage("");
    setFullNameErrorMessage("");
    setDateOfBirthErrorMessage("");

    let isValid = true;

    if (email) {
      if (!Validations.email.regex.test(email!)) {
        setEmailErrorMessage(localization.valid_email);
        isValid = false;
      }
    }

    if (mobile) {
      if (!Validations.mobile.regex.test(mobile!)) {
        setMobileErrorMessage(localization.mobile_validation_error);
        isValid = false;
      }
    }

    if (fullName) {
      if (!Validations.fullName.regex.test(fullName!)) {
        setFullNameErrorMessage(localization.only_character_allows);
        isValid = false;
      }
    }

    if (dateOfBirth) {
      const [year, month, day] = dateOfBirth!.split('-').map(Number);
      if (!checkIsAdult(year, month, day)) {
        setDateOfBirthErrorMessage(localization.age_validation);
        isValid = false;
      }
    }

    return isValid;
  };

  const handleUpdateProfile = async () => {
    if (!checkValidation()) {
      return;
    }

    let avatarFileName = avatar ?? null;

    if (uploadedAvatar) {
      var response = await userService.uploadUserAvatar({
        file: uploadedAvatar,
        fileName: uploadedAvatar.name,
        oldFileName: profile?.profilePictureUrl ?? null,
      });
      if (!response.isSuccess) return;

      avatarFileName = response.data?.fileName ?? null;

    }

    const data: UserProfileUpdateRequest = {
      userName: username,
      name: fullName,
      mobile: mobile,
      birthdate: dateOfBirth ? formatDate(dateOfBirth) : undefined,
      countryCode: countryCode,
      preferredContactMethodsCsv: preferredContactMethodsCsv,
      address: address,
      email: email,
      profilePictureUrl: avatarFileName,
    };

    userService.updateUserProfile(data).then((response) => {
      if (response.isSuccess) {
        dispatch(fetchUserProfile());
        setIsEditMode(false);
        toastService.success(
          response.message ?
            localization.getString(response.message as keyof typeof localization) :
            localization.profile_updated_successfully,
        );
      } else {
        toastService.error(response.message ?
          localization.getString(response.message as keyof typeof localization) :
          "Failed to update profile");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const handleNotificationChange = (method: string, checked: boolean) => {
    setIsChanged(true);
    switch (method) {
      case ContactMethods.ALL:
        if (profile?.emailVerified && profile?.mobileVerified) {
          setIsAllNotifications(checked);
          setIsSMSNotifications(checked);
          setIsEmailNotifications(checked);
          setIsWhatsAppNotifications(checked);
          setIsCallNotifications(checked);
        } else if (!profile?.emailVerified && !profile?.mobileVerified) {
          toast.warning(localization.verify_all_email_preferences);
        } else if (profile?.emailVerified && !profile?.mobileVerified) {
          toast.warning(localization.verify_mobile_preferences);
        } else {
          toast.warning(localization.verify_email_preferences);
        }
        break;

      case ContactMethods.SMS:
        if (profile?.mobileVerified) {
          setIsSMSNotifications(checked);
        } else {
          toast.warning(localization.verify_mobile_preferences);
        }
        break;

      case ContactMethods.EMAIL:
        if (profile?.emailVerified) {
          setIsEmailNotifications(checked);
        } else {
          toast.warning(localization.verify_email_preferences);
        }
        break;

      case ContactMethods.WHATSAPP:
        if (profile?.mobileVerified) {
          setIsWhatsAppNotifications(checked);
        } else {
          toast.warning(localization.verify_mobile_preferences);
        }
        break;

      case ContactMethods.CALL:
        if (profile?.mobileVerified) {
          setIsCallNotifications(checked);
        } else {
          toast.warning(localization.verify_mobile_preferences);
        }
        break;
    }
  };

  const handleChangePassword = () => {
    setOpenChangePassword(true);
  };

  const handleUpdateUserBiometrics = async () => {
    const data: UpdateUserBiometricsRequest = {
      userId: profile?.id ?? "",
      password: biometricsVerificationPassword,
      isBiometricsEnabled: !isBiometricsEnabled,
    };
    var response = await userService.updateUserBiometrics(data);
    if (response.isSuccess) {
      setShowBiometricsPasswordModal(false);
      setIsBiometricsEnabled(!isBiometricsEnabled);
      toast.success(localization.biometrics_updated_successfully);
    } else {
      setShowBiometricsPasswordModal(false);
      toast.error(response.message);
    }
  };

  const handleBiometricsToggle = async (checked: boolean) => {
    setBiometricsVerificationPassword(null);
    if (checked) {
      let isBiometricsEnabled = localStorageService.getItem<boolean>("biometricsEnabled");
      if (isBiometricsEnabled && isBiometricsEnabled === true) {
        setShowBiometricsPasswordModal(true);
      } else {
        toast.warning(localization.please_setup_biomatrics);
      }
    } else {
      await handleUpdateUserBiometrics();
    }
  };

  const getAvatarSrc = () => {
    if (!avatar) {
      return "/images/avatars/avatar5.svg";
    }
    else if (getAvatarById(avatar)) {
      return getAvatarById(avatar) ?? "/images/avatars/avatar5.svg";
    }
    else if (avatar.includes('blob')) {
      return avatar;
    }
    return `${BLOB_IMAGE_URL}${BlobContainerConsts.UserAvatar}/${avatar}`;

  };

  const handleCloseAvatarDrawer = () => {
    setOpenChooseAvatar(false);
    setAvatar(profile?.profilePictureUrl ?? "");
  };

  let content = (
    <div className="px-4 w-full">

      <div className="flex justify-end z-10 mx-auto">
        <Button
          type="button"
          className="flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-white text-sm"
          onClick={handleEdit}
        >
          {isEditMode ? (
            <>
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>{localization.back}</span>
            </>
          ) : (
            <>
              <img src="/icons/ic-edit.svg" alt="edit" className="w-4 h-4" />
              <span>{localization.edit}</span>
            </>
          )}
        </Button>
      </div>

      <div className="w-full max-w-4xl mx-auto">

        {/* Avatar */}
        <div className={`${deviceType === DeviceType.Web ? 'flex items-center justify-center' : ''}`}>
          <div className="relative w-32 h-32">
            <img
              src={getAvatarSrc()}
              alt="avatar"
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              className="w-32 h-32 rounded-full object-cover"
            />

            <Button
              type="button"
              className={`absolute bottom-0 right-0 w-8 h-8 rounded-full
              ${isEditMode
                  ? "bg-black/70 text-white hover:bg-black/80"
                  : "bg-transparent cursor-default"
                }
              flex items-center justify-center`}
              onClick={() => setOpenChooseAvatar(true)}
            >
              {isEditMode && (
                <img src="/icons/ic-edit.svg" alt="edit" className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-6">

          <TextInput
            label={localization.username}
            value={username}
            placeholder={localization.enter_user_name}
            isRequired
            disabled
            onChange={(e) => setUsername(e.target.value)}
          />

          <TextInput
            label={localization.full_name}
            value={fullName ?? ""}
            placeholder={localization.enter_full_name}
            disabled={!isEditMode || profile?.name !== null}
            onChange={(e) => handleFullNameChange(e.target.value)}
            error={fullNameErrorMessage}
          />

          <div className="flex flex-col">
            <TextInput
              label={localization.email}
              placeholder={localization.enter_email}
              value={email ?? ""}
              disabled={!isEditMode || (profile?.email !== null && isEmailVerified)}
              error={emailErrorMessage}
              iconSrc={
                profile?.email && profile?.email === email
                  ? isEmailVerified
                    ? "/icons/ic-verified.svg"
                    : "/icons/ic-pending.svg"
                  : ""
              }
              onChange={(e) => handleEmailChange(e.target.value)}
            />

            {profile?.email && !isEmailVerified && profile?.email === email && (
              <Button
                type="button"
                className="self-end text-sm text-[#E7A11D] hover:underline cursor-pointer"
                onClick={handleSendEmailVerificationCode}
              >
                {localization.verify_your_email}
              </Button>
            )}
          </div>

          <DatePicker
            label={localization.date_of_birth}
            placeholder={localization.enter_birthdate}
            value={dateOfBirth ?? ""}
            error={dateOfBirthErrorMessage}
            onChange={handleDateOfBirthChange}
            allowedTextInput={false}
            disableFutureDates
            disabled={!isEditMode || profile?.birthdate !== null}
            variant="default"
            alwaysOpenDown={deviceType === DeviceType.Web}
          />

          <div className="flex flex-col">
            <PhoneNumberInput
              label={localization.mobile}
              placeholder={localization.enter_mobile}
              value={mobile ?? ""}
              showCountryCode
              countryCode={countryCode}
              onCountryChange={setCountryCode}
              error={mobileErrorMessage}
              onChange={handleMobileChange}
              iconSrc={
                profile?.mobile && profile?.mobile === mobile
                  ? isMobileVerified
                    ? "/icons/ic-verified.svg"
                    : "/icons/ic-pending.svg"
                  : ""
              }
              disabled={
                !isEditMode ||
                (profile?.mobile !== null && isMobileVerified)
              }
              maxLength={10}
              minLength={10}
            />

            {profile?.mobile && !isMobileVerified && profile?.mobile === mobile && (
              <Button
                type="button"
                className="self-end text-sm text-[#E7A11D] hover:underline cursor-pointer"
                onClick={handleSendMobileVerificationCode}
              >
                {localization.verfiy_your_number}
              </Button>
            )}
          </div>

          <TextInput
            label={localization.address}
            placeholder={localization.address}
            value={address ?? ""}
            disabled={!isEditMode}
            onChange={(e) => handleAddressChange(e.target.value)}
          />

          {userDeviceStore.getState().accessModeSource.sourceType === AccessMode.App && (
          <div className="bg-black border border-[#1F1F1F] rounded-lg">
            <ToggleSwitch
              label={localization.is_biomatrics}
              checked={isBiometricsEnabled}
              showBorder={false}
              onChange={() => {
                handleBiometricsToggle(!isBiometricsEnabled);
              }}
              disabled={!isEditMode}
            />
          </div>
          )}

        </div>

        <div className="mt-12">
          <Header4 className="font-semibold tracking-wider mb-4">
            {localization.Contact_Preferences}
          </Header4>

          <div className="w-full bg-black rounded-md border border-[#1F1F1F]">
            <ToggleSwitch
              label={localization.all}
              checked={isAllNotifications}
              onChange={() =>
                handleNotificationChange(ContactMethods.ALL, !isAllNotifications)
              }
              disabled={!isEditMode}
            />
            <ToggleSwitch
              label={localization.SMS}
              checked={isSMSNotifications}
              onChange={() =>
                handleNotificationChange(ContactMethods.SMS, !isSMSNotifications)
              }
              disabled={!isEditMode}
            />
            <ToggleSwitch
              label={localization.email}
              checked={isEmailNotifications}
              onChange={() =>
                handleNotificationChange(ContactMethods.EMAIL, !isEmailNotifications)
              }
              disabled={!isEditMode}
            />
            <ToggleSwitch
              label={localization.Call}
              checked={isCallNotifications}
              onChange={() =>
                handleNotificationChange(ContactMethods.CALL, !isCallNotifications)
              }
              disabled={!isEditMode}
            />
            <ToggleSwitch
              label={localization.Whatsapp_Telegram}
              checked={isWhatsAppNotifications}
              onChange={() =>
                handleNotificationChange(ContactMethods.WHATSAPP, !isWhatsAppNotifications)
              }
              disabled={!isEditMode}
            />
          </div>
        </div>

        <div className="mt-6">
          <span
            className="hover:underline cursor-pointer"
            onClick={handleChangePassword}
          >
            {localization.do_you_want_to_change_password}
          </span>
        </div>

        <div className="flex justify-center mt-8">
          <Button
            type="submit"
            className={`md:w-1/3 w-full py-4 rounded-lg text-white bg-primary-gradient
            ${!isEditMode || !isChanged
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-primary-gradient/80"
              }`}
            onClick={handleUpdateProfile}
            disabled={!isEditMode || !isChanged}
          >
            <span className="uppercase tracking-wider">
              {localization.submit}
            </span>
          </Button>
        </div>

      </div>

      <OtpModal
        open={showEmailVerificationModal}
        onSubmit={(otp) => handleVerifyEmailVerificationCode(otp)}
        showTimer
        errorMessage={emailVerificationErrorMessage}
        onResend={handleResendEmailVerificationCode}
        onClose={handleCloseEmailVerificationModal}
      />

      <OtpModal
        open={showMobileVerificationModal}
        onSubmit={(otp) => handleVerifyMobileVerificationCode(otp)}
        showTimer
        errorMessage={mobileVerificationErrorMessage}
        onResend={handleResendMobileVerificationCode}
        onClose={handleCloseMobileVerificationModal}
      />

      {showBiometricsPasswordModal && (
        <Modal isOpen={showBiometricsPasswordModal} onClose={() => setShowBiometricsPasswordModal(false)}>
          <PasswordInput
            label={localization.please_enter_password}
            placeholder={localization.password}
            variant="modal"
            value={biometricsVerificationPassword ?? ""}
            maxLength={30}
            onChange={(e) => setBiometricsVerificationPassword(e.target.value)}
          />

          <Button
            type="button"
            className="w-full rounded-lg bg-black py-3 font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleUpdateUserBiometrics}
            disabled={!biometricsVerificationPassword || biometricsVerificationPassword.length < 8}
          >
            {localization.submit}
          </Button>
        </Modal>
      )}

      {deviceType !== DeviceType.Web && (
        <>
          <BottomDrawer
            open={openChangePassword}
            onClose={() => setOpenChangePassword(false)}
          >
            <ChangePassword onClose={() => setOpenChangePassword(false)} />
          </BottomDrawer>

          <BottomDrawer
            open={openChooseAvatar}
            onClose={handleCloseAvatarDrawer}
          >
            <div className="p-6">
              <Avatar
                avatar={avatar ?? ""}
                onChange={handleChooseAvatar}
                onSubmit={(file) => handleSubmitAvatar(file)}
              />
            </div>
          </BottomDrawer>
        </>
      )}
    </div>
  );

  if (deviceType === DeviceType.Web && openChooseAvatar) {
    content = (
      <div className="p-6 w-full max-w-4xl mx-auto">
        <Header4 className="text-white mb-5 font-semibold text-center">{localization.select_your_avatar}</Header4>
        <div className="flex items-center justify-center">
          <div className="relative w-32 h-32">
            <img
              src={getAvatarSrc()}
              alt="avatar"
              className="w-32 h-32 rounded-full object-cover"
            />

            <Button
              type="button"
              className={`absolute bottom-0 right-0 w-8 h-8 rounded-full
                ${isEditMode && !openChooseAvatar
                  ? "bg-black/70 text-white hover:bg-black/80"
                  : "bg-transparent cursor-default"
                }
                flex items-center justify-center`}
              onClick={() => setOpenChooseAvatar(true)}
            >
              {isEditMode && !openChooseAvatar && (
                <img src="/icons/ic-edit.svg" alt="edit" className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        <Avatar
          avatar={avatar ?? ""}
          onChange={handleChooseAvatar}
          className="mt-8"
          onSubmit={(file) => handleSubmitAvatar(file)}
        />
      </div>
    );
  }

  if (deviceType === DeviceType.Web && openChangePassword) {
    content = (
      <div className="flex justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-4xl">
          <ChangePassword onClose={() => setOpenChangePassword(false)} />
        </div>
      </div>
    );
  }

  return (
    deviceType !== DeviceType.Web ? content : (
      <Card>
        <CardHeader showBackButton={openChooseAvatar || openChangePassword} contentInline={true} onBackButtonClick={() => {
          if (openChooseAvatar) {
            setOpenChooseAvatar(false);
          }
          if (openChangePassword) {
            setOpenChangePassword(false);
          }
        }} />

        <CardBody showGradient={true}>
          {content}
        </CardBody>
      </Card>
    )
  );
}